import type { Edge, Node } from '@xyflow/react'
import type { ActionNodeData, AutomationPayload, ConditionNodeData, TriggerNodeData } from './types'

/**
 * Wandelt eine Automatisierung aus der API in Knoten und Kanten für React Flow um.
 */
export function automationToGraph(automation?: Partial<AutomationPayload>): {
  nodes: Node[]
  edges: Edge[]
} {
  const nodes: Node[] = []
  const edges: Edge[] = []

  const triggerEvent = automation?.triggerEvent ?? 'tasks.completed'

  // 1. Trigger-Knoten
  const triggerId = 'node-trigger-1'
  nodes.push({
    id: triggerId,
    type: 'trigger',
    position: { x: 60, y: 120 },
    data: { event: triggerEvent },
  })

  // 2. Bedingungs-Knoten
  const rawConditions = automation?.conditions ?? {}
  const conditionEntries = Object.entries(rawConditions)
  const conditionIds: string[] = []

  let yOffset = 80
  for (let i = 0; i < conditionEntries.length; i++) {
    const entry = conditionEntries[i]
    if (!entry) continue
    const [key, value] = entry
    const condId = `node-condition-${i + 1}`
    conditionIds.push(condId)

    nodes.push({
      id: condId,
      type: 'condition',
      position: { x: 440, y: yOffset },
      data: {
        key,
        value,
      },
    })

    // Kante von Trigger zu Bedingung
    edges.push({
      id: `edge-${triggerId}-${condId}`,
      source: triggerId,
      sourceHandle: 'trigger-out',
      target: condId,
      targetHandle: 'cond-in',
      animated: true,
      type: 'editable',
    })

    yOffset += 140
  }

  // 3. Aktions-Knoten
  const rawActions = automation?.actions ?? []
  const defaultActions =
    rawActions.length > 0 ? rawActions : [{ type: 'notification.send', recipients: 'guardians' }]

  let actionY = 80
  for (let j = 0; j < defaultActions.length; j++) {
    const act = defaultActions[j]
    if (!act) continue
    const actId = `node-action-${j + 1}`

    nodes.push({
      id: actId,
      type: 'action',
      position: { x: 820, y: actionY },
      data: { ...act },
    })

    // Kante: wenn Bedingungen existieren, von allen Bedingungen zur Aktion; sonst direkt vom Trigger
    if (conditionIds.length > 0) {
      for (const condId of conditionIds) {
        edges.push({
          id: `edge-${condId}-${actId}`,
          source: condId,
          sourceHandle: 'cond-out',
          target: actId,
          targetHandle: 'action-in',
          animated: true,
          type: 'editable',
        })
      }
    } else {
      edges.push({
        id: `edge-${triggerId}-${actId}`,
        source: triggerId,
        sourceHandle: 'trigger-out',
        target: actId,
        targetHandle: 'action-in',
        animated: true,
        type: 'editable',
      })
    }

    actionY += 140
  }

  return { nodes, edges }
}

/**
 * Wandelt die Knoten und Kanten aus dem React Flow Canvas zurück in die
 * API-Nutzlast einer Automatisierung.
 */
export function graphToAutomation(
  nodes: Node[],
  edges: Edge[],
  meta: { name: string; enabled: boolean },
): { success: true; automation: AutomationPayload } | { success: false; fehler: string } {
  const name = meta.name.trim()
  if (!name) {
    return { success: false, fehler: 'Bitte gib der Regel eine Bezeichnung.' }
  }

  const triggerNodes = nodes.filter((n) => n.type === 'trigger')
  if (triggerNodes.length === 0) {
    return { success: false, fehler: 'Es muss genau ein Auslöser-Baustein vorhanden sein.' }
  }
  if (triggerNodes.length > 1) {
    return { success: false, fehler: 'Mehrere Auslöser in einem Graphen werden nicht unterstützt.' }
  }

  const triggerNode = triggerNodes[0]
  if (!triggerNode) {
    return { success: false, fehler: 'Auslöser-Baustein konnte nicht geladen werden.' }
  }
  const triggerData = triggerNode.data as TriggerNodeData
  const triggerEvent = triggerData.event ?? 'tasks.completed'

  // Erreichbarkeits-Graph aufbauen (bidirektional traversierbar für maximale Nutzerfreundlichkeit)
  const adjacency = new Map<string, string[]>()
  for (const edge of edges) {
    const targets = adjacency.get(edge.source) ?? []
    targets.push(edge.target)
    adjacency.set(edge.source, targets)

    const revTargets = adjacency.get(edge.target) ?? []
    revTargets.push(edge.source)
    adjacency.set(edge.target, revTargets)
  }

  const visited = new Set<string>()
  const queue = [triggerNode.id]
  visited.add(triggerNode.id)

  while (queue.length > 0) {
    const currentId = queue.shift()
    if (!currentId) break
    const neighbors = adjacency.get(currentId) ?? []
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor)
        queue.push(neighbor)
      }
    }
  }

  // Bedingungen und Aktionen entlang erreichbarer Knoten sammeln
  const conditions: Record<string, unknown> = {}
  const actions: Array<Record<string, unknown>> = []

  for (const node of nodes) {
    if (!visited.has(node.id)) continue

    if (node.type === 'condition') {
      const data = node.data as ConditionNodeData
      if (typeof data.key === 'string' && data.value !== undefined) {
        conditions[data.key] = data.value
      }
    } else if (node.type === 'action') {
      const data = node.data as ActionNodeData
      if (typeof data.type === 'string') {
        const actionObj: Record<string, unknown> = { type: data.type }
        if (data.type === 'allowance.bonus') {
          actionObj.recipient = data.recipient ?? 'actor'
          actionObj.amountCents = data.amountCents ?? 50
        } else if (data.type === 'shopping.add_ingredients') {
          if (typeof data.listId === 'string') actionObj.listId = data.listId
        } else {
          actionObj.recipients = data.recipients ?? 'guardians'
        }
        actions.push(actionObj)
      }
    }
  }

  if (actions.length === 0) {
    return {
      success: false,
      fehler:
        'Es wurde keine verbundene Aktion gefunden. Bitte verbinde den Auslöser über eine Kante mit mindestens einer Aktion.',
    }
  }

  return {
    success: true,
    automation: {
      name,
      triggerEvent,
      conditions,
      actions,
      enabled: meta.enabled,
    },
  }
}
